import { createContext, useContext, useEffect, useRef, useState } from 'react';

const SocketContext = createContext(null);
const appUrl = process.env.REACT_APP_SOCKET_URL;

export const SocketProvider = ({ children, token, chatId }) => {
  const [messages, setMessages] = useState([]);
  const [isRespLoading, setIsRespLoading] = useState(false);
  const socketRef = useRef(null);
  const reconnectIntervalRef = useRef(null);
  const animateLoadingRef = useRef(true);
  const inactiveTimeoutRef = useRef(null);
  const lastMessageRef = useRef(null);
  const isStreamingRef = useRef(false); // Track if currently receiving streaming chunks
  const streamTimeoutRef = useRef(null); // Timeout to detect end of streaming

  const connectWebSocket = () => {
    if (!token || !chatId || document.hidden) {
      console.warn('Token or Chat ID is missing');
      return;
    }

    const wsUrl = `${appUrl}?token=${token}&chat_id=${chatId}`;
    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      clearInterval(reconnectIntervalRef.current);
      reconnectIntervalRef.current = null;
    };

    ws.onmessage = (event) => {
      const chunk = event.data;

      // Clear any existing timeout
      if (streamTimeoutRef.current) {
        clearTimeout(streamTimeoutRef.current);
      }

      // Hide loading ellipsis as soon as first chunk arrives
      setIsRespLoading(false);

      // Handle streaming chunks
      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1];

        // If we're currently streaming and last message is from assistant, append the chunk
        if (isStreamingRef.current && lastMsg && lastMsg.role === 'assistant' && lastMsg.isStreaming) {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMsg,
              message: lastMsg.message + chunk
            }
          ];
        }

        // Otherwise, start a new streaming message (first chunk)
        isStreamingRef.current = true;
        return [
          ...prev,
          {
            message: chunk,
            role: 'assistant',
            isStreaming: true,
            isTyping: false // Disable typing animation for streaming
          }
        ];
      });

      // Set a timeout to mark streaming as complete after 1 second of inactivity
      streamTimeoutRef.current = setTimeout(() => {
        if (isStreamingRef.current) {
          setMessages((prev) => {
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && lastMsg.role === 'assistant' && lastMsg.isStreaming) {
              return [
                ...prev.slice(0, -1),
                {
                  ...lastMsg,
                  isStreaming: false,
                  isComplete: true
                }
              ];
            }
            return prev;
          });
          isStreamingRef.current = false;
        }
      }, 1000); // 1 second of no chunks = streaming complete

      // Disable animation loading flag
      animateLoadingRef.current = false;
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);

      // Mark streaming as complete and finalize the message
      if (isStreamingRef.current) {
        setMessages((prev) => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === 'assistant' && lastMsg.isStreaming) {
            return [
              ...prev.slice(0, -1),
              {
                ...lastMsg,
                isStreaming: false,
                isTerminated: true // Mark as terminated due to error
              }
            ];
          }
          return prev;
        });
        isStreamingRef.current = false;
      }

      setIsRespLoading(false);
    };

    ws.onclose = () => {
      console.log('WebSocket closed');

      // Finalize any streaming message when connection closes
      if (isStreamingRef.current) {
        setMessages((prev) => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === 'assistant' && lastMsg.isStreaming) {
            return [
              ...prev.slice(0, -1),
              {
                ...lastMsg,
                isStreaming: false,
                isComplete: true // Mark as complete
              }
            ];
          }
          return prev;
        });
        isStreamingRef.current = false;
      }

      setIsRespLoading(false);
      attemptReconnect();
    };
  };

  const attemptReconnect = () => {
    if (
      reconnectIntervalRef.current ||
      !token ||
      !chatId ||
      document.hidden ||
      !isRespLoading
    )
      return;

    reconnectIntervalRef.current = setInterval(() => {
      if (
        !socketRef.current ||
        socketRef.current.readyState === WebSocket.CLOSED
      ) {
        if (!document.hidden) {
          console.log('Attempting to reconnect...');
          connectWebSocket();
        }
      }
    }, 3000); // Try reconnect every 3 seconds
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      console.log('Tab hidden, starting inactivity timer...');

      if (!inactiveTimeoutRef.current) {
        inactiveTimeoutRef.current = setTimeout(() => {
          if (document.hidden && !isRespLoading) {
            console.log('Tab hidden for 5 minutes — closing WebSocket');
            socketRef.current?.close();
            clearInterval(reconnectIntervalRef.current);
            reconnectIntervalRef.current = null;
          }
          inactiveTimeoutRef.current = null;
        }, 5 * 60 * 1000); //5 min
      }
    } else {
      console.log('Tab is active — clearing inactivity timer');
      clearTimeout(inactiveTimeoutRef.current);
      inactiveTimeoutRef.current = null;

      // Only reconnect if socket is closed or doesn't exist
      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
        connectWebSocket();
      }
    }
  };

  useEffect(() => {
    // Only connect if we don't have an open connection already
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      connectWebSocket();
    }

    const handleBeforeUnload = () => {
      socketRef.current?.close();
      clearInterval(reconnectIntervalRef.current);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Only close socket on actual unmount or when token/chatId changes
      // Don't close if it's just a re-render with same dependencies
      if (socketRef.current) {
        socketRef.current.close();
      }
      clearInterval(reconnectIntervalRef.current);
      clearTimeout(inactiveTimeoutRef.current);
      clearTimeout(streamTimeoutRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [token, chatId]);

  useEffect(() => {
    // Always clear messages when chatId changes
    setMessages([]);
    animateLoadingRef.current = true;
    setIsRespLoading(false);
    isStreamingRef.current = false; // Reset streaming state
    clearTimeout(streamTimeoutRef.current); // Clear any pending stream timeout

    // Close WebSocket when switching to new chat (no chatId)
    if (!chatId && socketRef.current) {
      socketRef.current.close();
    }
  }, [chatId]);

  const sendMessage = (text) => {
    // Prevent duplicate message sending
    if (isRespLoading) {
      console.warn('Already sending a message, ignoring duplicate');
      return;
    }

    // Prevent sending the same message twice in quick succession
    if (lastMessageRef.current === text) {
      console.warn('Same message sent too quickly, ignoring');
      return;
    }

    lastMessageRef.current = text;
    animateLoadingRef.current = true;
    isStreamingRef.current = false; // Reset streaming state before new message

    setTimeout(() => {
      if (!token || !chatId) return;
      const messageObj = { message: text, role: 'user' };

      if (
        socketRef.current &&
        socketRef.current.readyState === WebSocket.OPEN
      ) {
        socketRef.current.send(text);
        setIsRespLoading(true);
      } else {
        setIsRespLoading(false);
        console.warn('WebSocket is not open.');
      }

      // Prevent duplicate user messages
      setMessages((prev) => {
        // Check if this exact message already exists
        const isDuplicate = prev.some(msg =>
          msg.message === text && msg.role === 'user'
        );

        if (isDuplicate) {
          console.warn('Duplicate user message detected, ignoring');
          return prev;
        }

        return [...prev, messageObj];
      });

      // Clear the last message ref after a delay
      setTimeout(() => {
        lastMessageRef.current = null;
      }, 2000);
    }, 1000);
  };

  return (
    <SocketContext.Provider
      value={{
        socket: socketRef.current,
        messages,
        sendMessage,
        isRespLoading,
        hasAnimateLoading: animateLoadingRef.current,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => useContext(SocketContext);
